import { writable, derived, get } from 'svelte/store';

import { env } from '$lib/config/environment';

/**
 * Who is signed in, if anybody.
 *
 * # Nothing here runs unless accounts are switched on
 *
 * `env.features.accounts` is false by default, and while it is the store starts
 * empty, restores nothing and writes nothing. The app behaves exactly as it did
 * before accounts existed. See `.env.example`.
 *
 * # The token lives in localStorage
 *
 * This is a static single-page app: there is no server of ours between the
 * browser and the API, so a session cookie has nothing to set it. The token
 * goes in localStorage and rides on an `Authorization` header, which is also
 * what ainapi's middleware reads — deliberately not a cookie, because a
 * credential the browser attaches on its own is what CSRF needs to work.
 *
 * The cost is that anything which can run script on this origin can read it.
 * That is the accepted trade for a static app, and it is why the token is
 * short-lived (twelve hours by default) rather than a long-lived one.
 *
 * # Storage is detected, not imported
 *
 * `browser` from `$app/environment` is the idiomatic SvelteKit answer and is
 * deliberately not used: `vitest.config.ts` leaves SvelteKit out of the test
 * build on purpose, so `$app/*` does not resolve there and a store that
 * imported it could not be tested at all. Asking whether there is a
 * localStorage is also the more honest question — that is the thing this
 * module actually needs.
 *
 * # An expired session is dropped on load, not on first refusal
 *
 * `expiresAt` comes back with the token, so a session that has already lapsed
 * is discarded before anything is rendered. Otherwise the app starts up looking
 * signed in, sends one request, and only then finds out — which shows the
 * customer their name in the corner and a 401 underneath it.
 */

/** What ainapi says a signed-in account is. */
export interface Organisation {
  name: string;
  role?: string;
  balance: {
    allowance: number;
    allowance_expires_at?: string;
    topup: number;
    total: number;
  };
}

export interface Account {
  id: string;
  email: string;
  full_name?: string;
  company?: string;
  approved: boolean;
  subscription: {
    status: string;
    entitled: boolean;
    expires_at?: string;
  };
  organisation?: Organisation;
}

export interface Session {
  token: string;
  /** RFC 3339, as the API sends it. */
  expiresAt: string;
  account: Account;
}

/**
 * Where the session is kept between page loads.
 *
 * Versioned, so that a change to the shape of what is stored does not have to
 * cope with reading the old one: a key nobody wrote is a customer who signs in
 * again, which is a far smaller problem than a half-parsed session.
 */
const STORAGE_KEY = 'ainova.session.v1';

/** The signed-in session, or null. */
export const session = writable<Session | null>(null);

/** Whether somebody is signed in. */
export const signedIn = derived(session, ($session) => $session !== null);

/** The signed-in account, or null. */
export const account = derived(session, ($session) => $session?.account ?? null);

/**
 * What the account's organisation has to spend, or null.
 *
 * Null both for a visitor and for an account holding no seat — the two are the
 * same to a screen deciding whether to show a balance, and the difference is
 * ainapi's to explain when something is actually spent.
 */
export const balance = derived(
  session,
  ($session) => $session?.account.organisation?.balance ?? null
);

/**
 * Whether the API is sending this caller unmasked data.
 *
 * # One flag, because there is now one rule
 *
 * Signing in lifts everything — the company name, the person's name, their
 * social links, and every address and telephone number. Signed out, all of it
 * is masked. There is no third state, so there is no second flag.
 *
 * This was two: `companyNamesInClear` and `contactDataInClear`, from when
 * signing in bought the company and the person was what an order was placed
 * for. Both had come to be `session !== null`, which made them two names for
 * one boolean and an invitation to ask the wrong one.
 *
 * # Why the components still ask, rather than the shell deciding
 *
 * It is tempting to say a members-only screen never masks and delete the check.
 * A session can expire while somebody is looking at one: the screen is still
 * there, the API has already started answering with masked rows, and a
 * component that had stopped asking would render `xxx` as though it were an
 * address and a scrambled name as though it were real.
 *
 * So masking follows the *session*, and layout follows the shell. They are
 * different questions and only one of them can go stale mid-visit.
 */
export const dataInClear = derived(session, ($session) => $session !== null);

/** Whether this build can keep anything between page loads. */
function hasStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

/** Whether a session has lapsed by now. */
function expired(candidate: Session, now: number): boolean {
  const at = Date.parse(candidate.expiresAt);
  // An unparseable date is treated as expired. A session we cannot date is one
  // we cannot promise anything about, and signing in again is cheap.
  return Number.isNaN(at) || at <= now;
}

/**
 * Read the stored session back, dropping anything stale or unreadable.
 *
 * Called once at startup. Returns null and clears the key on anything it cannot
 * make sense of, so a bad value cannot wedge the app on every load.
 */
export function restoreSession(now: number = Date.now()): Session | null {
  if (!hasStorage() || !env.features.accounts) return null;

  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;

  let candidate: Session;
  try {
    candidate = JSON.parse(raw) as Session;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }

  if (!candidate?.token || !candidate?.account || expired(candidate, now)) {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }

  session.set(candidate);
  return candidate;
}

/** Store a session and remember it across loads. */
export function startSession(next: Session): void {
  session.set(next);
  if (hasStorage() && env.features.accounts) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }
}

/**
 * Replace the account on the current session, keeping the token.
 *
 * For a refreshed `/auth/me`: the balance moves as credits are spent, and the
 * screen showing it should not have to sign anybody in again to catch up.
 * Does nothing when nobody is signed in, rather than inventing a session
 * around an account.
 */
export function updateAccount(next: Account): void {
  const current = get(session);
  if (!current) return;
  startSession({ ...current, account: next });
}

/**
 * Replace the organisation's balance after something was spent.
 *
 * The balance is derived from the session, so it cannot be set directly — and
 * it should not be, because what is stored has to move with it or a reload
 * would show the figure from before the spend.
 *
 * Does nothing for an account with no organisation: there is no balance to
 * correct, and inventing one around a spend that could not have happened would
 * be worse than leaving it alone.
 */
export function updateBalance(next: Organisation['balance']): void {
  const current = get(session);
  if (!current?.account.organisation) return;

  updateAccount({
    ...current.account,
    organisation: { ...current.account.organisation, balance: next }
  });
}

/** Forget the session, here and in storage. */
export function endSession(): void {
  session.set(null);
  if (hasStorage()) {
    // Not conditional on the flag: a build that had accounts switched on and
    // then off again should still be able to clear what it left behind.
    localStorage.removeItem(STORAGE_KEY);
  }
}

/**
 * The bearer token for an outgoing request, or null.
 *
 * Null once the session has lapsed, so a request is sent anonymously rather
 * than with a credential the API is about to refuse.
 */
export function authToken(now: number = Date.now()): string | null {
  const current = get(session);
  if (!current || expired(current, now)) return null;
  return current.token;
}

/**
 * The `Authorization` header for an outgoing request, or nothing.
 *
 * Spread into a request's headers. Returns an empty object rather than an
 * undefined header so a caller can use it unconditionally:
 *
 *     apiClient.get('/credits', undefined, { headers: authHeader() })
 */
export function authHeader(): Record<string, string> {
  const token = authToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
